import React, { useState, useEffect, useCallback } from 'react';
import { X, Save, Info, AlertCircle, Dumbbell, Plus, Trash2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import { useAnimations } from '../../hooks/useAnimations';
import MuscleGroupSelector from '../common/MuscleGroupSelector';

/** Palette allineata ai modali palestra (Create/Edit workout, scheda). */
const ImprovedUserExerciseForm = ({ exercise, onClose, onSuccess, embedded = false }) => {
  const { isDark } = useTheme();
  const { currentUser } = useAuth();
  const { t } = useTranslation();
  const { getAnimationClasses, getInteractionClasses } = useAnimations();

  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  const [formData, setFormData] = useState({
    nome: '',
    descrizione: '',
    attrezzatura: '',
    is_cardio: 0,
    primary_muscle_id: null,
    secondary_muscles: []
  });

  useEffect(() => {
    if (exercise) {
      setFormData({
        nome: exercise.nome || '',
        descrizione: exercise.descrizione || '',
        attrezzatura: exercise.attrezzatura || '',
        is_cardio: Number(exercise.is_cardio || exercise.is_isometric) === 1 ? 1 : 0,
        primary_muscle_id: exercise.primary_muscle_id || null,
        secondary_muscles: exercise.secondary_muscles ? exercise.secondary_muscles.map(sm => ({
          muscle_id: sm.id,
          activation_level: sm.activation_level || 'high',
          notes: sm.notes || ''
        })) : []
      });
    }
  }, [exercise]);

  const validateForm = useCallback(() => {
    const errors = {};

    if (!formData.nome.trim()) {
      errors.nome = 'Il nome dell\'esercizio è obbligatorio';
    } else if (formData.nome.trim().length < 2) {
      errors.nome = 'Il nome deve essere di almeno 2 caratteri';
    }

    if (formData.descrizione.trim() && formData.descrizione.trim().length < 10) {
      errors.descrizione = 'La descrizione deve essere di almeno 10 caratteri';
    }

    if (!formData.primary_muscle_id) {
      errors.primary_muscle_id = 'È necessario selezionare almeno un muscolo primario';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData.nome, formData.descrizione, formData.primary_muscle_id]);

  useEffect(() => {
    validateForm();
  }, [formData.nome, formData.descrizione, formData.primary_muscle_id, validateForm]);

  const inputClass = (hasError) =>
    `w-full rounded-lg border px-3 py-2 text-sm transition-colors focus:outline-none focus:ring-2 ${
      isDark
        ? 'border-gray-700 bg-gray-800 text-white placeholder:text-white/45 focus:ring-indigo-500/35'
        : 'border-[#dad8e5] bg-white text-[#3a3a3a] placeholder:text-[#3a3a3a]/45 focus:ring-[#372d83]/40'
    } ${hasError ? 'border-red-500 ring-1 ring-red-500/30' : ''}`;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    let newValue;
    if (type === 'checkbox') {
      newValue = checked ? 1 : 0;
    } else if (type === 'number') {
      newValue = parseFloat(value) || 0;
    } else {
      newValue = value;
    }

    setFormData({ ...formData, [name]: newValue });

    if (name === 'descrizione') {
      const errors = {};
      if (newValue.trim() && newValue.trim().length < 10) {
        errors.descrizione = 'La descrizione deve essere di almeno 10 caratteri';
      }
      setValidationErrors(prev => ({ ...prev, descrizione: errors.descrizione }));
    } else {
      setTimeout(() => validateForm(), 100);
    }
  };

  const handlePrimaryMuscleChange = (muscleId) => {
    const newFormData = { ...formData, primary_muscle_id: muscleId };
    setFormData(newFormData);

    const errors = {};
    if (!muscleId) {
      errors.primary_muscle_id = 'È necessario selezionare almeno un muscolo primario';
    }
    setValidationErrors(prev => ({ ...prev, primary_muscle_id: errors.primary_muscle_id }));
  };

  const handleAddSecondaryMuscle = () => {
    setFormData({
      ...formData,
      secondary_muscles: [
        ...formData.secondary_muscles,
        { muscle_id: null, activation_level: 'high', notes: '' }
      ]
    });
  };

  const handleRemoveSecondaryMuscle = (index) => {
    const newSecondaryMuscles = formData.secondary_muscles.filter((_, i) => i !== index);
    setFormData({ ...formData, secondary_muscles: newSecondaryMuscles });
  };

  const handleSecondaryMuscleChange = (index, field, value) => {
    const newSecondaryMuscles = [...formData.secondary_muscles];
    newSecondaryMuscles[index] = {
      ...newSecondaryMuscles[index],
      [field]: value
    };
    setFormData({ ...formData, secondary_muscles: newSecondaryMuscles });
  };

  const handleSubmit = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (!validateForm()) {
      setMessage('Correggi gli errori prima di salvare');
      setMessageType('warning');
      return;
    }

    setLoading(true);

    try {
      const dataToSend = {
        ...formData,
        created_by_user_id: currentUser.user_id,
      };

      if (exercise && exercise.id) {
        dataToSend.id = exercise.id;
        dataToSend.user_id = currentUser.user_id;
      } else {
        dataToSend.status = 'pending_review';
      }

      const method = exercise && exercise.id ? 'PUT' : 'POST';

      const response = await fetch('/api/custom_exercise_standalone.php', {
        method: method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(dataToSend),
      });

      const result = await response.json();

      if (result.success) {
        setMessage(exercise && exercise.id
          ? t('userExerciseForm.messages.updateSuccess')
          : t('userExerciseForm.messages.createSuccess')
        );
        setMessageType('success');
        if (onSuccess) onSuccess(result);
      } else {
        setMessage(result.message || t('userExerciseForm.errors.saveError'));
        setMessageType('error');
      }
    } catch (error) {
      console.error('Errore nel salvataggio esercizio:', error);
      setMessage('Errore nel salvataggio dell\'esercizio');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const messageBoxClass =
    messageType === 'success'
      ? 'border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-500/30 dark:bg-emerald-950/40 dark:text-emerald-100'
      : messageType === 'warning'
        ? 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-500/30 dark:bg-amber-950/40 dark:text-amber-100'
        : 'border-red-200 bg-red-50 text-red-900 dark:border-red-500/30 dark:bg-red-950/40 dark:text-red-100';

  const musclePanelClass = isDark
    ? 'border-gray-600/60 bg-gray-800/25'
    : 'border-[#dad8e5] bg-[#eeebf5]/80';

  const secondaryRowClass = isDark
    ? 'border-gray-800 bg-gray-800/60'
    : 'border-[#dad8e5] bg-white';

  const labelClass = 'mb-2 block text-sm font-medium text-[#3a3a3a] dark:text-white/90';
  const sectionTitleClass = 'mb-4 flex items-center text-base font-semibold text-[#3a3a3a] dark:text-white';

  const formInner = (
    <>
      {message && (
        <div className={`mb-4 rounded-lg border p-4 text-sm ${messageBoxClass}`}>
          {message}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-2 lg:gap-8">
          {/* Colonna sinistra: dati base */}
          <div className="min-w-0 space-y-4">
            <h3 className={sectionTitleClass}>
              <Info className="mr-2 h-5 w-5 shrink-0 text-emerald-600 dark:text-gray-300" />
              {t('userExerciseForm.basicInfo')}
            </h3>

            <div>
              <label className={labelClass}>
                {t('userExerciseForm.exerciseName')} *
              </label>
              <input
                type="text"
                name="nome"
                value={formData.nome}
                onChange={handleChange}
                className={inputClass(!!validationErrors.nome)}
                placeholder={t('userExerciseForm.exerciseNamePlaceholder')}
              />
              {validationErrors.nome && (
                <p className="mt-1 flex items-center text-sm text-red-600 dark:text-red-400">
                  <AlertCircle className="mr-1 h-4 w-4" />
                  {validationErrors.nome}
                </p>
              )}
            </div>

            <div>
              <label className={labelClass}>
                {t('userExerciseForm.description')} ({t('common.optional')})
              </label>
              <textarea
                name="descrizione"
                value={formData.descrizione}
                onChange={handleChange}
                className={`${inputClass(!!validationErrors.descrizione)} min-h-[88px]`}
                rows={3}
                placeholder={t('userExerciseForm.descriptionPlaceholder')}
              />
              {validationErrors.descrizione && (
                <p className="mt-1 flex items-center text-sm text-red-600 dark:text-red-400">
                  <AlertCircle className="mr-1 h-4 w-4" />
                  {validationErrors.descrizione}
                </p>
              )}
            </div>

            <div>
              <label className={labelClass}>
                {t('userExerciseForm.equipment')}
              </label>
              <input
                type="text"
                name="attrezzatura"
                value={formData.attrezzatura}
                onChange={handleChange}
                className={inputClass(false)}
                placeholder={t('userExerciseForm.equipmentPlaceholder')}
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                name="is_cardio"
                id="is_cardio_uef"
                checked={formData.is_cardio === 1}
                onChange={handleChange}
                className="h-4 w-4 rounded border-[#dad8e5] text-[#372d83] focus:ring-[#372d83]/50 dark:border-white/20 dark:bg-gray-800 dark:text-gray-300 dark:focus:ring-[#c9c2e8]/30"
              />
              <label htmlFor="is_cardio_uef" className="ml-2 block text-sm text-[#3a3a3a] dark:text-white/85">
                {t('userExerciseForm.cardioExercise')}
              </label>
            </div>
          </div>

          {/* Colonna destra: muscoli */}
          <div className={`min-w-0 rounded-xl border-2 border-dashed p-4 ${musclePanelClass}`}>
            <div className="mb-3 flex items-center justify-between gap-2">
              <label className="block text-sm font-semibold text-[#3a3a3a] dark:text-white">
                🎯 {t('userExerciseForm.advancedMuscleSystem')} ({t('common.optional')})
              </label>
              <span className="rounded-full bg-[#eeebf5] px-2 py-1 text-xs font-medium text-[#372d83] dark:bg-indigo-900/40 dark:text-[#e8e4f5]">
                {t('userExerciseForm.new')}
              </span>
            </div>

            <div className="space-y-4">
              <div>
                <label className={labelClass}>
                  {t('userExerciseForm.primaryMuscle')} *
                </label>
                <MuscleGroupSelector
                  variant="gym"
                  value={formData.primary_muscle_id}
                  onChange={handlePrimaryMuscleChange}
                  mode="single"
                  placeholder="Seleziona muscolo primario..."
                  showHierarchy={true}
                />
                {validationErrors.primary_muscle_id && (
                  <p className="mt-1 flex items-center text-sm text-red-600 dark:text-red-400">
                    <AlertCircle className="mr-1 h-4 w-4" />
                    {validationErrors.primary_muscle_id}
                  </p>
                )}
                <p className="mt-1 text-xs text-[#3a3a3a]/70 dark:text-white/55">
                  {t('userExerciseForm.primaryMuscleHelper')}
                </p>
              </div>

              <div>
                <div className="mb-2 flex items-center justify-between gap-2">
                  <label className="block text-sm font-medium text-[#3a3a3a] dark:text-white/90">
                    {t('userExerciseForm.secondaryMuscles')}
                  </label>
                  <button
                    type="button"
                    onClick={handleAddSecondaryMuscle}
                    className={`flex items-center gap-1 rounded-lg px-3 py-1 text-xs font-medium transition-colors ${
                      isDark
                        ? 'bg-gray-800 text-white/90 hover:bg-gray-700'
                        : 'bg-white text-[#3a3a3a] shadow-sm ring-1 ring-[#dad8e5] hover:bg-[#faf9fc]'
                    }`}
                  >
                    <Plus className="h-3 w-3" />
                    {t('userExerciseForm.addSecondaryMuscle')}
                  </button>
                </div>

                {formData.secondary_muscles.length > 0 ? (
                  <div className="space-y-2">
                    {formData.secondary_muscles.map((sm, index) => (
                      <div key={index} className={`rounded-lg border p-3 ${secondaryRowClass}`}>
                        <div className="flex flex-wrap items-start gap-2">
                          <div className="min-w-0 flex-1">
                            <MuscleGroupSelector
                              variant="gym"
                              value={sm.muscle_id}
                              onChange={(muscleId) => handleSecondaryMuscleChange(index, 'muscle_id', muscleId)}
                              mode="single"
                              placeholder={t('userExerciseForm.placeholders.selectSecondaryMuscle')}
                              showHierarchy={false}
                            />
                          </div>
                          <select
                            value={sm.activation_level}
                            onChange={(e) => handleSecondaryMuscleChange(index, 'activation_level', e.target.value)}
                            className={`rounded-lg border px-3 py-2 text-sm ${
                              isDark
                                ? 'border-gray-700 bg-gray-800 text-white'
                                : 'border-[#dad8e5] bg-white text-[#3a3a3a]'
                            }`}
                            title={t('userExerciseForm.activationLevel')}
                          >
                            <option value="high">{t('userExerciseForm.activationLevels.high')}</option>
                            <option value="medium">{t('userExerciseForm.activationLevels.medium')}</option>
                            <option value="low">{t('userExerciseForm.activationLevels.low')}</option>
                          </select>
                          <button
                            type="button"
                            onClick={() => handleRemoveSecondaryMuscle(index)}
                            className="rounded-lg p-2 text-red-600 transition-colors hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/40"
                            title={t('userExerciseForm.removeMuscle')}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="py-3 text-center text-sm italic text-[#3a3a3a]/60 dark:text-white/45">
                    {t('userExerciseForm.noSecondaryMuscles')}
                  </p>
                )}

                <p className="mt-2 text-xs text-[#3a3a3a]/70 dark:text-white/55">
                  {t('userExerciseForm.secondaryMusclesHelper')}
                </p>
              </div>
            </div>

            <div className={`mt-4 rounded-md border p-3 text-xs ${
              isDark
                ? 'border-gray-800 bg-gray-900/80 text-gray-300'
                : 'border-[#dad8e5] bg-[#faf9fc] text-[#3a3a3a]'
            }`}>
              <strong>{t('common.tips.title')}:</strong> {t('userExerciseForm.muscleSystemTip')}
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-4 border-t border-[#dad8e5] pt-6 dark:border-white/10 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm text-[#3a3a3a]/70 dark:text-white/55">
            {exercise && exercise.id ? t('userExerciseForm.update') : t('userExerciseForm.create')}{' '}
            {t('userExerciseForm.exercise').toLowerCase()}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-[#dad8e5] bg-white px-4 py-2 text-sm font-medium text-[#3a3a3a] transition-colors hover:bg-[#eeebf5] dark:border-white/15 dark:bg-gray-800 dark:text-white dark:hover:bg-indigo-700"
            >
              {message && messageType === 'success' ? t('common.close') : t('common.cancel')}
            </button>

            <button
              type="submit"
              disabled={loading || Object.keys(validationErrors).length > 0}
              className={`inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600 px-6 py-2 text-sm font-medium text-white transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-gray-400 dark:bg-indigo-600 dark:text-white dark:hover:bg-indigo-700 dark:disabled:opacity-50 ${getInteractionClasses()}`}
            >
              {loading ? (
                <>
                  <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-b-transparent" />
                  {t('userExerciseForm.saving')}
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  {exercise && exercise.id ? t('userExerciseForm.update') : t('userExerciseForm.create')}{' '}
                  {t('userExerciseForm.exercise')}
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    </>
  );

  if (embedded) {
    return <div className="w-full">{formInner}</div>;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div
        className={`flex max-h-[88vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-[#dad8e5] bg-white shadow-2xl dark:border-white/10 dark:bg-gray-900 dark:shadow-black/35 ${getAnimationClasses('fadeIn')}`}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-[#dad8e5] bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-4 dark:border-white/10 dark:from-gray-950 dark:via-gray-900 dark:to-gray-900">
          <div className="flex min-w-0 items-center gap-3">
            <div className="shrink-0 rounded-lg bg-white/20 p-2 dark:bg-white/10">
              <Dumbbell className="h-6 w-6 text-white" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-bold text-white sm:text-xl">
                {exercise && exercise.id ? t('userExerciseForm.editExercise') : t('userExerciseForm.createNewExercise')}
              </h2>
              <p className="truncate text-sm text-white/85">
                {exercise && exercise.id ? t('userExerciseForm.updateExerciseInfo') : t('userExerciseForm.addCustomExercise')}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-lg p-2 text-white/90 transition-colors hover:bg-white/10"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto bg-[#faf9fc] p-6 dark:bg-gray-900">
          {formInner}
        </div>
      </div>
    </div>
  );
};

export default ImprovedUserExerciseForm;
